import { useNavigate, useLocation } from 'react-router';
import { analytics } from '../services/analytics';
import React, {
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { FaCalculator } from 'react-icons/fa';
import { useIsMobile, useIsLargeScreen } from '../hooks/useMediaQuery';
import { Gift, Loader2 } from 'lucide-react';
import { TbFileInvoice, TbPigMoney } from 'react-icons/tb';
import { GoHome } from 'react-icons/go';
import { PiUsersFourLight } from 'react-icons/pi';
import { HiOutlineDocumentDuplicate } from 'react-icons/hi2';
import { MdDisplaySettings } from 'react-icons/md';
// TODO: Botón de contacto flotante deshabilitado - soporte se accede desde Configuración
// import { AiOutlineMessage } from 'react-icons/ai';

// Tabs del usuario
import { MiBeneficioV2 } from '../components/MiBeneficioV2';
import ConfigurarBeneficioV2 from './DashboardTabs/User/ConfigurarBeneficioV2';
import PlanEmpleoV2 from './DashboardTabs/User/PlanEmpleoV2';
import AjustesUserV2 from './DashboardTabs/User/AjustesUserV2';
import SimulatorsV2 from './DashboardTabs/User/SimulatorsV2';

// Tabs del usuario individual
import IndividualDashboardV2 from './DashboardTabs/User/IndividualDashboardV2';
import { marcarPlanVisto } from '../utils/resumenMulticuenta/planVisto';
import ResumenMulticuenta from './DashboardTabs/User/ResumenMulticuenta';
import useSeccionTraspasosEnabled from '../hooks/useSeccionTraspasosEnabled';
import { aterrizaEnResumen } from '../utils/resumenMulticuenta/aterrizaje';
import { navigateToPlan } from '../components/Navigation/planNavigation';
import TuPlanAutonomoV2 from './DashboardTabs/User/TuPlanAutonomoV2';
import { getMyPensionPlanTransfers } from '../api/pension-plan-transfers';
import getPlans, { UserPlanI } from '../api/users/getPlans';
import {
	isLivePlan,
	isPersonalViewPlan,
	resolveAvailableViews,
	resolveViewOptions
} from '../utils/viewOptions';
import { resolveActiveArcaPlan } from '../utils/resolveActiveArcaPlan';
import { ArcaPlan } from '../constants/arcaPlans';
import AportacionesIndividualV2 from './DashboardTabs/User/AportacionesIndividualV2';
import MyReferralsView from './DashboardTabs/User/MyReferralsView';

// Tabs del administrador V2
import InicioAdminV2 from './DashboardTabs/Admin/InicioAdminV2';
import PlantillaV2 from './DashboardTabs/Admin/PlantillaV2';
import ConfigurarBeneficioAdminV2 from './DashboardTabs/Admin/ConfigurarBeneficioAdminV2';
import AdminBillingV2 from './DashboardTabs/Admin/AdminBillingV2';
import AdminContribucionesV2 from './DashboardTabs/Admin/AdminContribucionesV2';
import AjustesAdminV2 from './DashboardTabs/Admin/AjustesAdminV2';

import { ArcaContext } from '../context/ArcaContext.context';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import getCurrentUser from '../api/users/getCurrentUser';
import EMPTY_CURRENT_USER, { CurrentUserI, UserPlanStatus } from '../api/users/interfaces/Users';
import { clearContextAndLocalStorage } from '../utils/clearLocalStorageAndContext';
import syncAllMovements from '../api/users/syncAllMovements';
import { AdhesionMode } from '../api/users/getAdhesionDetails';
import getCompanyInfoEppConfig from '../api/companies/getCompanyInfoEppConfig';
import checkFeatureFlagByName from '../api/feature-flags/checkFeatureFlagByName';
import { buildNavigationData } from './dashboardNavigation';
import dayjs from 'dayjs';
import AnnounceNewFeatureModal from '../components/ChakraUi/Modals/AnnounceNewFeatureModal';
import WelcomeV2Modal from '../components/Modals/WelcomeV2Modal';
import toggleFeatureFlagByName from '../api/feature-flags/toggleFeatureFlagByName';
import { Sidebar } from '../components/arca-nav/Sidebar';
import { MobileTopBar } from '../components/arca-nav/MobileTopBar';
import { MobileBottomNav } from '../components/arca-nav/MobileBottomNav';
import { barraInferior, seccionesDeNavegacion, textosDeNavegacion } from './navegacionProa';
import ContactSupportModal from '../components/Modals/ContactSupportModal';
import AIAssistantModal from '../components/Chat/AIAssistantModal';
import ContactDataBanner from '../components/ContactDataBanner/ContactDataBanner';
import InviteFriendModal from '../components/Referrals/InviteFriendModal';
import {
	AccountType,
	DashboardRoute,
	ViewType,
	hasAdminRole,
	pickTabOrFirst
} from '../types/dashboard';
import { isPureFreelanceUser } from '../utils/isPureFreelanceUser';
import { hasPendingPersonalPlan, liveFunnelEnrollments } from '../utils/funnelEnrollment';
import { consumeTokenLoginIntent } from '../utils/tokenLoginIntent';
import {
	MAGIC_LINK_ACTIONS,
	MagicLinkPreset,
	useMagicLinkContribution
} from '../hooks/useMagicLinkContribution';
import { PensionPlanTransfer } from '../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { resolveCompanyName } from '../utils/resolveCompanyName';
import { ageFromBirthdate } from '../utils/ageFromBirthdate';
import { ageFromFunnel } from '../utils/ageFromFunnel';

interface Tab {
	key: string;
	label: string;
	icon: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
	component: ReactNode;
	route: string;
}

interface DashboardProps {}

/** Lo que se espera a los traspasos antes de pintar sin edad: no bloquea la carga. */
const FUNNEL_AGE_TIMEOUT_MS = 2000;

/**
 * Edad aproximada con los años del funnel; `undefined` si no hay, si fallan los traspasos o si
 * tardan más de 2 s (está en el camino de la carga del Dashboard).
 */
async function ageFromFunnelTransfers(
	traspasos: ReturnType<typeof getMyPensionPlanTransfers>
): Promise<number | undefined> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	const tarde = new Promise<undefined>((resolve) => {
		timer = setTimeout(() => resolve(undefined), FUNNEL_AGE_TIMEOUT_MS);
	});
	const edad = traspasos
		.then((res) => (res?.success && res?.data ? ageFromFunnel(res.data) : undefined))
		.catch(() => undefined);
	return Promise.race([edad, tarde]).finally(() => clearTimeout(timer));
}

/** A qué plan va el importe del enlace del correo. `null`: a ninguno (no tiene dónde). */
type ContributionPresetTarget =
	| { kind: 'EMPLOYMENT' }
	| { kind: 'PERSONAL'; planId: string | null };

/**
 * Dónde se edita la aportación del enlace del correo (Arca-Team/product#3, §3.2):
 * - Con `plan` y que sea una fila suya ACTIVA (la que puede recibirla), manda `plan`.
 * - Si no, con plan de empleo activo, el de empleo (también con un personal al lado).
 * - Si no, el primer personal activo.
 * - Sin la lista de planes (`/plans` caído), deciden los flags de la persona.
 */
const resolveContributionPresetTarget = (
	plans: UserPlanI[] | undefined,
	planId: string | undefined,
	isPersonalUser: boolean
): ContributionPresetTarget | null => {
	if (plans === undefined) {
		return isPersonalUser ? { kind: 'PERSONAL', planId: null } : { kind: 'EMPLOYMENT' };
	}
	const activePlans = plans.filter((plan) => plan.status === 'active');
	const chosen = activePlans.find((plan) => plan.id === planId);
	if (chosen) {
		return isPersonalViewPlan(chosen)
			? { kind: 'PERSONAL', planId: chosen.id }
			: { kind: 'EMPLOYMENT' };
	}
	if (activePlans.some((plan) => !isPersonalViewPlan(plan))) return { kind: 'EMPLOYMENT' };
	const firstPersonal = activePlans.find(isPersonalViewPlan);
	return firstPersonal ? { kind: 'PERSONAL', planId: firstPersonal.id } : null;
};

/** La página de soporte, en el menú de la cuenta. */
const SUPPORT_URL = 'https://support.arcadigital.app/';

/** Lo más que se espera a saber a qué vista va antes de quitar el spinner (Arca-Team/product#55). */
export const ESPERA_MAXIMA_DEL_ATERRIZAJE_MS = 5000;

const Dashboard: React.FC<DashboardProps> = () => {
	const { token, companyId, userId } = useContext(ArcaContext);
	const navigate = useNavigate();
	const location = useLocation();
	const { userAccountType, language } = useContext(ArcaContext);

	const {
		state: { translate }
	} = useContext(I18nContext);

	const myBenefit = `${translate('user.dashboard.myBenefit')}`;
	const myBenefitIndividual = `${translate('user.dashboard.myBenefitIndividual')}`;
	const resumenTabKey = `${translate('resumen.tab')}`;
	const myReferrals = `${translate('user.dashboard.myReferrals')}`;
	const adminMyContributions = `${translate('admin.dashboard.myContributions')}`;
	const employmentPlan = `${translate('user.dashboard.employmentPlan')}`;
	const simulators = `${translate('user.dashboard.simulators')}`;
	const tuPlan = `${translate('user.dashboard.tuPlan')}`;
	const userSettings = `${translate('user.dashboard.userSettings')}`;
	const individualSettings = `${translate('user.dashboard.individualSettings')}`;
	const home = `${translate('admin.dashboard.home')}`;
	const staff = `${translate('admin.dashboard.staff')}`;
	const setBenefit = `${translate('admin.dashboard.setBenefit')}`;
	const adminSettings = `${translate('admin.dashboard.adminSettings')}`;
	const billing = `${translate('admin.dashboard.billing')}`;

	// Defino los tabs por defecto para el usuario y el administrador
	const USER_DEFAULT_TAB = myBenefit;
	const ADMIN_DEFAULT_TAB = home;

	// Define estos breakpoints de forma reactiva (custom hooks replacing Chakra)
	const isMobileScreen = useIsMobile();
	const isLargeScreen = useIsLargeScreen();

	// Inicializa el estado del menú con un valor por defecto
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	// Actualiza isMenuOpen cuando cambie el tamaño de la pantalla
	useEffect(() => {
		setIsMenuOpen(isLargeScreen);
	}, [isLargeScreen]);

	const [activeTab, setActiveTab] = useState(
		userAccountType === AccountType.BOTH || userAccountType === AccountType.ADMIN
			? ADMIN_DEFAULT_TAB
			: USER_DEFAULT_TAB
	);
	const [viewType, setViewType] = useState<string>(
		userAccountType === AccountType.BOTH || userAccountType === AccountType.ADMIN
			? ViewType.ADMIN
			: ViewType.USER
	);
	/**
	 * La vista ACTUAL, para leerla después de un `await`. El efecto que recarga
	 * `getCurrentUser` captura `viewType` al arrancar; si la persona cambia de
	 * vista mientras la petición vuela, al resolver pisaría lo que eligió.
	 */
	const viewTypeRef = useRef(viewType);
	useEffect(() => {
		viewTypeRef.current = viewType;
	}, [viewType]);

	const [isChatOpen, setIsChatOpen] = useState(false);
	const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
	const [inviteModalSource, setInviteModalSource] = useState<
		'sidebar' | 'mobile_nav' | 'dashboard_cta'
	>('sidebar');

	const [loadingUser, setLoadingUser] = useState(true);
	const [currentUser, setCurrentUser] = useState<CurrentUserI>(EMPTY_CURRENT_USER);

	// UX SCAFFOLD (tarea Notion "Definimos MVP rescates") — pestaña "Aportaciones"
	// renombrada a "Movimientos" para todos los usuarios, no solo quien ya
	// rescató: también registra rescates, no solo aportaciones.
	const userMyContributions = translate('rescue.movementsTab.label');

	const [isFreelance, setIsFreelance] = useState<boolean>(false);
	const [isIndividual, setIsIndividual] = useState<boolean>(false);
	/**
	 * TODOS los planes del usuario (`GET /users/plans`), tal cual los sirve el
	 * backend. `undefined` mientras no se sabe, y cada lector decide qué hacer
	 * con eso:
	 *   · el selector (`resolveViewOptions`) falla en ABIERTO: conserva la
	 *     entrada de empleado de siempre;
	 *   · la vista de arranque (`resolveAvailableViews`) igual: sin lista sólo
	 *     cuentan los roles, y al montar la lista aún no ha llegado;
	 *   · el modal de referidos falla en CERRADO salvo que la vista personal ya
	 *     esté en pantalla (`showIndividualTabs`).
	 * Una lista VACÍA es una respuesta: «no tiene planes».
	 */
	const [plans, setPlans] = useState<UserPlanI[] | undefined>(undefined);
	// `GET /users/plans` en vuelo. Va aparte de `plans` porque un fallo también deja `undefined`
	// (fail-open): la rentabilidad del plan espera a que esto acabe, no a que haya lista.
	const [plansLoading, setPlansLoading] = useState(true);
	/**
	 * Las peticiones de quien NO es individual, para saber si tiene un plan
	 * individual EN PROCESO (campaña de traspaso B2B). `undefined` mientras no se
	 * saben; si la llamada falla, lista vacía: sin «en proceso», como hoy.
	 */
	const [funnelTransfers, setFunnelTransfers] = useState<PensionPlanTransfer[] | undefined>(
		undefined
	);
	/**
	 * ¿Ha llegado por su enlace personal? Se lee UNA vez, al montar: si se esperara a
	 * tener los datos, un `/plans` caído dejaría la marca guardada y abriría el plan en
	 * proceso en una entrada posterior con contraseña.
	 */
	const [arrivedByTokenLink] = useState(() => consumeTokenLoginIntent());
	/**
	 * Enlace del correo con una acción (Arca-Team/product#3). Se consume al montar, como la
	 * marca de arriba, y se enruta UNA vez cuando ya se saben el usuario y sus planes.
	 */
	const magicLinkPreset = useMagicLinkContribution();
	const magicLinkHandled = useRef(false);
	/** Euros del correo para el modal de Mi beneficio (B2B), hasta que lo abre. */
	const [employmentContributionPreset, setEmploymentContributionPreset] = useState<number | null>(
		null
	);
	/** Euros del correo para el modal mensual de la vista personal (B2C), hasta que lo abre. */
	const [personalContributionPreset, setPersonalContributionPreset] = useState<number | null>(null);
	const arrivalHandled = useRef(false);
	/**
	 * Sus traspasos al entrar, pedidos UNA vez y en cuanto se sabe quién es: los usan la edad y el
	 * plan individual en proceso. Antes se pedían dos veces, la segunda al final de la carga, y era
	 * una ida y vuelta más antes de poder decidir el aterrizaje (Arca-Team/product#55). Las recargas
	 * de después (`loadFunnelTransfers`) piden de nuevo.
	 */
	const traspasosDelArranque = useRef<{
		token: string;
		promesa: ReturnType<typeof getMyPensionPlanTransfers>;
	} | null>(null);
	const pedirTraspasosDelArranque = (authToken: string) => {
		if (traspasosDelArranque.current?.token !== authToken) {
			traspasosDelArranque.current = {
				token: authToken,
				promesa: getMyPensionPlanTransfers(authToken)
			};
		}
		return traspasosDelArranque.current.promesa;
	};
	/** El aterrizaje en el Resumen se decide UNA vez: después, manda lo que elija la persona. */
	const aterrizajeEnResumenHecho = useRef(false);
	/**
	 * Ya se sabe a qué vista va (Arca-Team/product#55). Hasta entonces, el spinner: si no, se pinta
	 * la vista por defecto un instante y luego salta al Resumen. Se decide una vez; un cambio de
	 * idioma no lo vuelve a poner a `false`.
	 */
	const [destinoDecidido, setDestinoDecidido] = useState(false);
	/** Pasó la espera máxima: se decide con lo que se sepa (una petición colgada no deja el spinner). */
	const [esperaAgotada, setEsperaAgotada] = useState(false);
	const [selectedPersonalPlanId, setSelectedPersonalPlanId] = useState<string | null>(null);
	// Su edad: de su fecha de nacimiento o, si no, aproximada con los años del funnel. Nunca una
	// supuesta, que pediría los tramos del ciclo de vida de otra edad (Arca-Team/product#32).
	const [currentAge, setCurrentAge] = useState<number | undefined>(undefined);
	// Plan resuelto del autónomo — para mostrar la pestaña "Tu plan" solo a los
	// planes Indexa de autónomos (los únicos con info de plan migrada de momento).
	const [freelanceArcaPlan, setFreelanceArcaPlan] = useState<ArcaPlan | null>(null);

	const [announceModalParams, setAnnounceModalParams] = useState<{
		featureFlagName: string;
		isOpen: boolean;
		mainRoleForFeature: 'USER' | 'ADMIN';
		title: string;
		description: string;
		imageSrc: string;
		path?: string;
		link?: string;
	}>({
		featureFlagName: '',
		isOpen: false,
		title: '',
		description: '',
		imageSrc: '',
		mainRoleForFeature: 'USER',
		link: ''
	});

	const [showWelcomeV2, setShowWelcomeV2] = useState(false);
	const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
	// Permite abrir el asistente Noé desde cualquier vista (p. ej. el CTA de la FAQ en
	// ExplorationView) sin acoplar el estado: se dispara un evento global de window.
	useEffect(() => {
		const openAssistant = () => setIsAIAssistantOpen(true);
		window.addEventListener('arca:open-assistant', openAssistant);
		return () => window.removeEventListener('arca:open-assistant', openAssistant);
	}, []);

	useEffect(() => {
		const isCompanyOnlyAccount =
			userAccountType === AccountType.ADMIN || userAccountType === AccountType.MANAGEMENT;
		const getCurrentUserAsync = async () => {
			try {
				const currentUser = await getCurrentUser(token);
				const currentUserData = currentUser.data as CurrentUserI;
				setCurrentUser(currentUserData);
				// Sus traspasos, ya: van en paralelo con el resto de la carga (mismo criterio que
				// `pidePeticionesDelFunnel` y que la edad de abajo).
				if (
					!isCompanyOnlyAccount &&
					!currentUserData?.user?.isIndividual &&
					!isPureFreelanceUser(currentUserData?.user)
				) {
					pedirTraspasosDelArranque(token).catch(() => undefined);
				}

				analytics.setUserProperties({
					email: currentUserData?.user?.email,
					name: currentUserData?.user?.name,
					isFreelance: currentUserData?.user?.isFreelance,
					isIndividual: currentUserData?.user?.isIndividual
				});

				setIsFreelance(currentUserData?.user?.isFreelance);
				// Sin fecha de nacimiento, la edad aproximada con los años hasta la jubilación que dijo
				// en el funnel (product#32). Antes de pintar las pestañas: así no piden los tramos dos veces.
				// Las cuentas solo de empresa no tienen pestañas personales: no se espera nada por ellas.
				setCurrentAge(
					ageFromBirthdate(currentUserData?.user?.birthdate) ??
						(isCompanyOnlyAccount
							? undefined
							: await ageFromFunnelTransfers(pedirTraspasosDelArranque(token)))
				);
				const userOnboard = currentUser.data.user.completedProfile;

				const userCompletedProfile = currentUserData?.user?.completedProfile;

				// Verificar si el usuario es individual
				const isIndividualUser = currentUserData?.user?.isIndividual;
				setIsIndividual(isIndividualUser);

				// Quien está en el Resumen sigue en él al recargar el usuario (cambio de idioma).
				const sigueEnResumen =
					viewTypeRef.current === ViewType.RESUMEN && resumenDisponibleRef.current;

				if (isIndividualUser) {
					// Para usuarios individuales, siempre usar vista USER
					const newViewType = sigueEnResumen ? ViewType.RESUMEN : ViewType.USER;
					const defaultTab = sigueEnResumen ? resumenTabKey : myBenefitIndividual;
					setViewType(newViewType);
					setActiveTab(defaultTab);

					// Sync del plan individual (puebla `pension_plan_contributions`, la tabla
					// que lee la vista de Aportaciones). Este `return` saltaba el sync de más
					// abajo → la tabla quedaba vacía → 0 aportaciones aunque Caser sí tuviera
					// la aportación. Se pasa el `mode` correcto (espejo de getAdhesionDetails):
					// freelance PURO → FREELANCE; individual puro → INDIVIDUAL.
					// Sin perfil completado el usuario aún no ha elegido/activado ningún plan
					// → no hay nada que sincronizar (bug PROD: cuentas a medio onboarding
					// disparaban el sync igual y generaban 500/400 de ruido sin motivo).
					if (userCompletedProfile) {
						const indivUser = currentUserData?.user;
						const indivMode: AdhesionMode = isPureFreelanceUser(indivUser)
							? 'FREELANCE'
							: 'INDIVIDUAL';
						// Fire-and-forget: el sync puebla `pension_plan_contributions` (la tabla que lee
						// la tab de Aportaciones) pero NO es necesario para pintar la vista Personal.
						// Sacarlo del gate evita que el spinner full-screen espere a la llamada mas lenta.
						// `Promise.resolve(...)` protege por si el sync no devolviera promesa.
						void Promise.resolve(syncAllMovements(token, indivMode)).catch((error) => {
							console.error('Error syncing individual movements:', error);
						});
					}

					setLoadingUser(false);
					return;
				}

				// Helper: consulta un feature flag y devuelve su estado activo, tragándose
				// cualquier error (flag ausente / red) como `false`.
				const safeCheckFlag = async (name: string): Promise<boolean> => {
					try {
						const ffRes = await checkFeatureFlagByName(
							token,
							companyId,
							currentUserData.user.id,
							name
						);
						return !!(ffRes?.success && ffRes.data?.isActive === true);
					} catch {
						return false;
					}
				};

				// autoenrollment se necesita ANTES del redirect de onboarding, así que se
				// espera en solitario. El resto de llamadas van en paralelo más abajo.
				const autoenrollment = await safeCheckFlag('autoenrollment');
				if (!userOnboard && !isIndividualUser && userCompletedProfile && autoenrollment) {
					navigate('/auto-enrollment');
					setLoadingUser(false);
					return;
				}

				if (!userOnboard && !isIndividualUser) {
					navigate('/onboarding-user');
					setLoadingUser(false);
					return;
				}

				const user = currentUserData?.user;
				const date = dayjs(user.lastMovementsUpdate);
				const userHasSynchronisedMovementsToday = date.isSame(dayjs(), 'day');

				// El sync que puebla `pension_plan_contributions` (tabla que lee la vista
				// de Aportaciones) debe correr con el MISMO mode que la lectura
				// (getAdhesionDetails). Sin mode, el backend deriva del estado físico y los
				// usuarios individuales/mixtos caen en EMPLOYMENT → el plan individual no se
				// sincroniza y la vista de Aportaciones sale a 0. Empleado B2B → sin mode
				// (deriva EMPLOYMENT, comportamiento previo correcto).
				// El societario (isFreelance + companyId) NO debe pedir FREELANCE — no tiene
				// plan autónomo propio, su plan real es el de EMPLEO de su empresa, que el
				// backend ya deriva solo cuando no se fuerza mode (bug 500/400 comparsa#645:
				// pedir FREELANCE sin plan reventaba/ensuciaba logs). isPureFreelanceUser
				// distingue al autónomo puro del societario (mismo helper usado arriba).
				const syncMode: AdhesionMode | undefined = isPureFreelanceUser(user)
					? 'FREELANCE'
					: user?.isIndividual
						? 'INDIVIDUAL'
						: undefined;

				// Fire-and-forget: sacamos el sync (la llamada más lenta) del gate de
				// loading para que la vista pinte de inmediato. Para individual/freelance
				// forzamos el sync con mode aunque ya se haya sincronizado hoy: un sync
				// previo SIN mode pudo poblar el plan equivocado (el backend re-sincroniza
				// al recibir mode; su guard es `&& !mode`).
				if (syncMode || !userHasSynchronisedMovementsToday) {
					void Promise.resolve(syncAllMovements(token, syncMode)).catch((error) => {
						console.error('Error syncing movements:', error);
					});
				}

				// viewType y activeTab ANTES de cualquier llamada de red más: sólo
				// dependen de `userAccountType`, los roles y los planes que ya se
				// conozcan. Calculados después quedaban a merced de que una excepción
				// del bloque saltase al `catch`, que pinta el dashboard
				// (`setLoadingUser(false)`) pero NO fija la vista — la cuenta mixta se
				// quedaba en ADMIN (regresión CI, run 30539972128).
				//
				// La vista ya elegida (leída por ref: puede haber cambiado durante el
				// `await`) se respeta si sigue permitida; sólo la cuenta que es SÓLO
				// empresa —admin puro o gestoría— se fuerza a la de empresa. Este
				// efecto se relanza al cambiar de idioma: forzar `USER` a toda cuenta
				// que no fuera BOTH devolvía a la vista de empleado a quien estaba en
				// Personal — y sin el rol `individual` el mixto sin admin ya no es BOTH.
				const candidateViewType = isCompanyOnlyAccount ? ViewType.ADMIN : viewTypeRef.current;

				// `AccountType` no dice QUÉ roles tiene el usuario: el backend deriva
				// BOTH de `roles.length > 1`, así que cualquier segundo rol llegaba
				// aquí como candidato a la vista de empresa. Validamos el candidato
				// contra las vistas que permiten de verdad sus roles y sus planes
				// (al montar la lista aún no ha llegado: entonces sólo cuentan los
				// roles, y el efecto de más abajo reconcilia cuando llega) y, si no
				// encaja, caemos a la primera disponible.
				const availableViews = resolveAvailableViews(
					currentUserData?.user?.roles,
					plans,
					pendingPersonalPlan
				);
				const newViewType = sigueEnResumen
					? ViewType.RESUMEN
					: availableViews.includes(candidateViewType as ViewType)
						? candidateViewType
						: availableViews[0];

				// Home de cada vista. La personal tiene la suya: fijarle la B2B dejaba
				// `activeTab` apuntando a una pestaña que no existe en su set.
				const defaultTab =
					newViewType === ViewType.RESUMEN
						? resumenTabKey
						: newViewType === ViewType.ADMIN
							? ADMIN_DEFAULT_TAB
							: newViewType === ViewType.INDIVIDUAL || isPureFreelanceUser(currentUserData?.user)
								? myBenefitIndividual
								: USER_DEFAULT_TAB;
				setViewType(newViewType);
				setActiveTab(defaultTab);

				// Config de empresa (multi-company): los freelances PUROS no tienen empresa
				// accesible (companyId placeholder -> backend 500); el societario SI la pide.
				//
				// ⚠️ Su respuesta ya no se consume: alimentaba el selector de empresas
				// del sidebar, que sale de la barra en esta tarea por no tener ni un
				// usuario (`user_memberships` no tiene una sola fila con rol admin).
				// La llamada se conserva porque está anclada por los tests de regresión
				// del societario post-flip; retirarla es una decisión aparte.
				if (companyId && !isPureFreelanceUser(user)) {
					await getCompanyInfoEppConfig(token, companyId).catch((err) => {
						console.error('Error fetching company config:', err);
						return null;
					});
				}

				setLoadingUser(false);

				// Check welcome V2 feature flag
				try {
					const welcomeRes = await checkFeatureFlagByName(
						token,
						companyId,
						currentUserData.user.id,
						'v2-platform-welcome'
					);
					if (
						welcomeRes?.success &&
						welcomeRes.data?.isActive &&
						!welcomeRes.data?.userHasSeenFeature
					) {
						setShowWelcomeV2(true);
					}
				} catch {
					// silently ignore — non-blocking
				}
			} catch (error) {
				console.error('Error loading user data:', error);
				setLoadingUser(false);
			}
		};

		getCurrentUserAsync();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [language, userAccountType, token]);

	// `GET /users/plans` — fuente única de "qué planes tiene este usuario", desde
	// BBDD y sin llamar a Caser en vivo. Se pide UNA vez al montar el Dashboard.
	//
	// De esta respuesta salen las entradas del selector de vista y el plan que se
	// pinta. El `.catch()` vacío deja `plans` en `undefined`, que NO es lo mismo
	// que una lista vacía: significa «no se sabe», y el selector conserva
	// entonces el comportamiento de siempre. Eso cubre el 404 de desplegar este
	// front antes que el backend, y un fallo de red.
	//
	// El endpoint iba con `@Roles('user')`: una cuenta que SOLO es admin o
	// gestora no tiene ese rol y recibiría un 403 en cada montaje —invisible
	// para ella por el `.catch()`, pero ruido real en el backend—. No se pide lo
	// que ya se sabe que va a ser rechazado. (comparsa ya no exige el rol en
	// `origin/master`; el cortocircuito se queda hasta verlo en PROD.)
	useEffect(() => {
		if (!token) return;
		// 🔴 No pedirlo NO es «no se sabe»: se sabe que no tiene planes propios, y
		// hay que decirlo con una lista vacía. Dejarlo en `undefined` mandaría al
		// fail-open y le pintaría a una gestoría la entrada de empleado que esta
		// pantalla viene justo a quitar.
		if (userAccountType === AccountType.ADMIN || userAccountType === AccountType.MANAGEMENT) {
			setPlans([]);
			setPlansLoading(false);
			return;
		}
		getPlans(token)
			.then((response) => {
				// `success: false` es el sobre de error del backend: eso es «no se
				// sabe», no «no tiene planes». Fail-open, igual que el `.catch()`.
				setPlans(response?.success && response?.data ? response.data : undefined);
			})
			.catch(() => undefined)
			.finally(() => setPlansLoading(false));
	}, [token, userAccountType]);

	/**
	 * Plan personal que está viendo el usuario.
	 *
	 * Se preselecciona el primero NO archivado que llega (el backend los sirve
	 * ordenados). No se persiste entre sesiones a propósito: un id guardado que
	 * ya no existe —plan archivado— dejaría la vista pidiendo un plan fantasma.
	 */
	/**
	 * Los PERSONALES vivos, para la vista personal: su plan de empleo tiene vista
	 * propia y no compite por este handle, y un archivado ya no se pinta.
	 */
	const personalPlans = useMemo(
		() => (plans ?? []).filter((plan) => isLivePlan(plan) && isPersonalViewPlan(plan)),
		[plans]
	);

	useEffect(() => {
		if (selectedPersonalPlanId) return;
		const first = personalPlans[0];
		if (first) setSelectedPersonalPlanId(first.id);
	}, [personalPlans, selectedPersonalPlanId]);

	/**
	 * El plan elegido, entero. La vista Personal necesita DOS cosas suyas y tienen
	 * que salir de la misma fila: el nombre que pinta y —sobre todo— su TIPO, que
	 * es el `mode` con el que se le piden los saldos a Caser.
	 *
	 * 🔴 Bajar solo el id fue el incidente del 02-sep-2026: la vista derivaba el
	 * `mode` del flag `is_freelance` del USUARIO mientras el id era el del plan
	 * preseleccionado. En quien tiene dos planes personales de tipo distinto los
	 * dos no coinciden, el backend cruza `type` con `id`, no encuentra fila y
	 * responde 400 — pantalla sin saldo.
	 */
	const selectedPersonalPlan = useMemo(
		() => personalPlans.find((plan) => plan.id === selectedPersonalPlanId) ?? null,
		[personalPlans, selectedPersonalPlanId]
	);
	// Su plan real aún no se sabe: o `/users/plans` está en vuelo, o ya llegó y el efecto de arriba
	// todavía no ha elegido el primero. En ese render Inicio usaría el plan del traspaso (que
	// puede ser otro) para la rentabilidad (chirigota#598). Solo mientras no hay id elegido: un id
	// que ya no está en la lista no puede dejar la pantalla en «cargando» para siempre.
	const personalPlanPending =
		plansLoading || (selectedPersonalPlanId === null && personalPlans.length > 0);

	const loadFunnelTransfers = useCallback(
		(authToken: string) =>
			getMyPensionPlanTransfers(authToken)
				.then((res) => setFunnelTransfers(res?.success && res?.data ? res.data : []))
				.catch(() => setFunnelTransfers([])),
		[]
	);

	// Peticiones de quien no es individual: de ellas sale el plan individual en
	// proceso. Un B2C ya tiene su vista individual por flags y no las necesita aquí.
	// Ni una cuenta solo de empresa ni un autónomo puro pueden tener plan individual
	// en proceso: la primera no tiene planes propios y el segundo ya opera su plan en
	// la vista individual. No se pide lo que no se va a usar.
	const pidePeticionesDelFunnel =
		!isIndividual &&
		userAccountType !== AccountType.ADMIN &&
		userAccountType !== AccountType.MANAGEMENT &&
		!isPureFreelanceUser(currentUser?.user);
	useEffect(() => {
		if (!token || loadingUser || !pidePeticionesDelFunnel) return;
		pedirTraspasosDelArranque(token)
			.then((res) => setFunnelTransfers(res?.success && res?.data ? res.data : []))
			.catch(() => setFunnelTransfers([]));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, loadingUser, pidePeticionesDelFunnel]);

	/**
	 * Plan individual EN PROCESO: alta del funnel viva y todavía ningún plan personal
	 * (hasta que operaciones ejecuta el alta no existe su fila en `user_epp_config`).
	 */
	const knownFunnelTransfers = funnelTransfers ?? [];
	const pendingPersonalPlan =
		!isIndividual &&
		funnelTransfers !== undefined &&
		hasPendingPersonalPlan(knownFunnelTransfers, plans);
	/** El plan que eligió en el funnel: nombra la entrada en proceso del selector. */
	const pendingPlanArcaId = pendingPersonalPlan
		? liveFunnelEnrollments(knownFunnelTransfers)
				.map((transfer) => transfer.selectedArcaPlanId)
				.find(Boolean) ?? null
		: null;
	/**
	 * El Resumen multicuenta (chirigota#585): 2+ planes (el personal en proceso cuenta) y la flag
	 * `seccion-traspasos`, la misma de las tarjetas de traspasos, para probarlo en PROD con pocas
	 * personas. Apagada, nadie lo ve: ni aterriza ni sale en el selector.
	 */
	const { enabled: seccionTraspasos, comprobada: flagComprobada } = useSeccionTraspasosEnabled(
		token,
		companyId,
		userId
	);
	const resumenDisponible = seccionTraspasos && aterrizaEnResumen({ plans, pendingPersonalPlan });
	/** Leído tras un `await` (la recarga del usuario al cambiar de idioma). */
	const resumenDisponibleRef = useRef(resumenDisponible);
	useEffect(() => {
		resumenDisponibleRef.current = resumenDisponible;
	}, [resumenDisponible]);
	/** Ya confirmó el plan: la tarjeta de Mi beneficio pasa a «ver el estado». */
	const pendingPersonalPlanLocked =
		pendingPersonalPlan &&
		liveFunnelEnrollments(knownFunnelTransfers).some((transfer) => !!transfer.planLockedAt);

	// Resolver el plan del autónomo (desde sus traspasos) para decidir si mostrar
	// la pestaña "Tu plan" — solo disponible para los planes Indexa de autónomos.
	useEffect(() => {
		if (!token || !isFreelance) return;
		getMyPensionPlanTransfers(token)
			.then((res) => {
				const transfers = res?.success && res?.data ? res.data : [];
				setFreelanceArcaPlan(resolveActiveArcaPlan(transfers) ?? null);
			})
			.catch(() => setFreelanceArcaPlan(null));
	}, [token, isFreelance]);

	// Navegación cuando se complete la carga de datos
	useEffect(() => {
		if (!loadingUser && destinoDecidido) {
			const firstTabRoute = tabsOfView(viewType)[0].route;
			navigate(`/dashboard/${firstTabRoute}`, { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadingUser, destinoDecidido, viewType, isIndividual]);

	// Sincronizar activeTab con la URL
	useEffect(() => {
		if (!loadingUser) {
			const path = location.pathname.split('/').pop() || '';
			const tab = findTabByRoute(path);
			if (tab) {
				setActiveTab(tab.key);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, viewType, loadingUser, isIndividual]);

	// Pestañas para el usuario.
	// Los tabs individuales se muestran tanto a usuarios B2C (isIndividual=true)
	// como a usuarios B2B con plan personal que han elegido ViewType.INDIVIDUAL
	// desde el selector del sidebar, y a los autónomos PUROS (isFreelance sin
	// companyId), migrados por completo a la vista individual. Un autónomo
	// SOCIETARIO (isFreelance + companyId) opera su plan de EMPLEO → se queda en
	// las tabs B2B (MiBeneficioV2) y su selector de vista muestra "empleado".
	const isPureFreelance = isPureFreelanceUser(currentUser?.user);
	const showIndividualTabs = isIndividual || isPureFreelance || viewType === ViewType.INDIVIDUAL;
	/**
	 * La vista personal de un plan que todavía no existe. Solo tiene su pestaña de
	 * inicio (decisión de producto, 15/09/26): Movimientos enseñaría el `planStatus`
	 * de su plan de EMPRESA, y Ajustes se monta en modo individual.
	 */
	const isPendingPersonalView =
		viewType === ViewType.INDIVIDUAL && pendingPersonalPlan && !isIndividual && !isPureFreelance;

	/**
	 * Quien entra por su enlace personal aterriza en su plan individual en proceso;
	 * con contraseña, en la vista de empresa. La marca se lee UNA vez, cuando ya se
	 * saben sus planes y sus peticiones, y solo abre la vista si de verdad hay plan en
	 * proceso. Mismo motivo que el efecto de abajo para ir antes del `return`.
	 */
	useEffect(() => {
		if (!arrivedByTokenLink || arrivalHandled.current) return;
		if (loadingUser || isIndividual || plans === undefined || funnelTransfers === undefined) return;
		arrivalHandled.current = true;
		if (!pendingPersonalPlan) return;
		setViewType(ViewType.INDIVIDUAL);
		navigateToDefaultTab(ViewType.INDIVIDUAL);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [arrivedByTokenLink, loadingUser, isIndividual, plans, funnelTransfers, pendingPersonalPlan]);

	const peticionesConocidas = !pidePeticionesDelFunnel || funnelTransfers !== undefined;

	/**
	 * Sin plan de empleo VIVO, la vista de empleado no tiene nada que enseñar.
	 *
	 * Al montar, los planes llegan DESPUÉS de fijar la vista de arranque (son
	 * efectos distintos), así que un ex-empleado con el empleo archivado y un
	 * plan personal vivo aterrizaba en la vista de empleado de un plan que ya no
	 * existe; su única entrada del selector era la personal, y con una sola el
	 * selector no se muestra: no había forma de salir. Mismo criterio que el
	 * selector (`resolveViewOptions`): sin lista, falla en abierto y no mueve a
	 * nadie. No toca a quien ya ve las tabs individuales por flags ni a Admin.
	 *
	 * Va aquí, antes del `return` del spinner, porque un hook no puede ir tras un
	 * return condicional; `navigateToDefaultTab` se declara más abajo y sólo se
	 * usa cuando `loadingUser` es false, con ese render ya completo.
	 */
	useEffect(() => {
		if (loadingUser || viewType !== ViewType.USER || showIndividualTabs) return;
		const options = resolveViewOptions(plans, currentUser?.user?.roles);
		if (options.some((option) => option.viewType === ViewType.USER)) return;
		if (!options.some((option) => option.viewType === ViewType.INDIVIDUAL)) return;
		setViewType(ViewType.INDIVIDUAL);
		navigateToDefaultTab(ViewType.INDIVIDUAL);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadingUser, viewType, showIndividualTabs, plans, currentUser]);

	/**
	 * El enlace del correo, una vez con el usuario y sus planes. Se enruta por `action`: hoy solo
	 * `increase_contribution`; otra acción se ignora y el dashboard queda como siempre. Otro
	 * enlace (p. ej. «Invita y gana» a su sección) es un caso más aquí.
	 *
	 * Manda sobre el aterrizaje (Resumen, plan en proceso del enlace personal): se marcan como
	 * decididos para que no lo pisen después. Mismo motivo que los de arriba para ir aquí.
	 */
	useEffect(() => {
		if (magicLinkHandled.current || !magicLinkPreset || loadingUser || plansLoading) return;
		magicLinkHandled.current = true;
		switch (magicLinkPreset.action) {
			case MAGIC_LINK_ACTIONS.INCREASE_CONTRIBUTION:
				routeContributionPreset(magicLinkPreset);
				break;
			default:
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [magicLinkPreset, loadingUser, plansLoading]);

	/**
	 * A qué vista va, decidido UNA vez y con todo ya sabido (Arca-Team/product#55): la flag, sus
	 * planes, sus peticiones del funnel y el enlace del correo. Hasta entonces se queda el spinner.
	 *
	 * Con 2+ planes (y la flag), aterriza en el Resumen multicuenta (chirigota#585, §3.1); después
	 * manda lo que elija la persona. Va detrás de los efectos del enlace personal del funnel y del
	 * correo, que corren antes en el mismo render y mandan sobre el Resumen: quien entra por su
	 * enlace personal va a su plan en proceso, y el del correo marca el aterrizaje como hecho.
	 *
	 * Un admin aterriza en su panel de empresa, como hasta ahora (decidido el 16/10), sin esperar a
	 * la flag ni a sus planes: el Resumen le queda al pasar a Personal.
	 *
	 * Si algo no responde nunca (las peticiones no tienen timeout), a los
	 * `ESPERA_MAXIMA_DEL_ATERRIZAJE_MS` se decide con lo que se sepa: sin flag o sin planes, donde
	 * siempre. Lo que llegue después (el enlace del correo, p. ej.) se enruta como hasta ahora.
	 */
	useEffect(() => {
		if (destinoDecidido || loadingUser) return;
		const espera = setTimeout(() => setEsperaAgotada(true), ESPERA_MAXIMA_DEL_ATERRIZAJE_MS);
		return () => clearTimeout(espera);
	}, [destinoDecidido, loadingUser]);
	useEffect(() => {
		if (destinoDecidido || loadingUser) return;
		const esAdmin = hasAdminRole(currentUser?.user?.roles);
		if (!esperaAgotada) {
			if (magicLinkPreset && !magicLinkHandled.current) return;
			if (!esAdmin && !flagComprobada) return;
			// Sin la flag no hay Resumen posible: ya se sabe a dónde va sin esperar a sus planes.
			if (!esAdmin && seccionTraspasos && (plansLoading || !peticionesConocidas)) return;
		}
		if (!aterrizajeEnResumenHecho.current) {
			aterrizajeEnResumenHecho.current = true;
			if (resumenDisponible && !esAdmin && !(arrivedByTokenLink && pendingPersonalPlan)) {
				setViewType(ViewType.RESUMEN);
				navigateToDefaultTab(ViewType.RESUMEN);
			}
		}
		setDestinoDecidido(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		destinoDecidido,
		loadingUser,
		esperaAgotada,
		magicLinkPreset,
		flagComprobada,
		seccionTraspasos,
		plansLoading,
		peticionesConocidas,
		resumenDisponible,
		arrivedByTokenLink,
		pendingPersonalPlan,
		currentUser
	]);

	const routeContributionPreset = ({ amount, plan }: MagicLinkPreset) => {
		if (!amount) return;
		const target = resolveContributionPresetTarget(plans, plan, isIndividual || isPureFreelance);
		if (!target) return;
		aterrizajeEnResumenHecho.current = true;
		arrivalHandled.current = true;
		if (target.kind === 'EMPLOYMENT') {
			// También a las cuentas BOTH/ADMIN, que arrancan en la vista de empresa.
			setViewType(ViewType.USER);
			navigateToDefaultTab(ViewType.USER);
			setEmploymentContributionPreset(amount);
			return;
		}
		if (target.planId) setSelectedPersonalPlanId(target.planId);
		// Un B2C o un autónomo puro ya ven sus pestañas personales en la vista USER.
		const personalView = isIndividual || isPureFreelance ? ViewType.USER : ViewType.INDIVIDUAL;
		setViewType(personalView);
		navigateToDefaultTab(ViewType.INDIVIDUAL);
		setPersonalContributionPreset(amount);
	};

	const allUserTabs: Tab[] = showIndividualTabs
		? [
				{
					key: myBenefitIndividual,
					label: myBenefitIndividual,
					icon: <GoHome />,
					component: (
						<IndividualDashboardV2
							selectedPersonalPlanId={selectedPersonalPlanId}
							selectedPlanDisplayName={selectedPersonalPlan?.planDisplayName ?? null}
							selectedPlanCatalogSlug={selectedPersonalPlan?.planCatalogSlug ?? null}
							plansLoading={personalPlanPending}
							selectedPlanType={selectedPersonalPlan?.type ?? null}
							pendingPersonalPlan={isPendingPersonalView}
							onFunnelRequestsChanged={
								isPendingPersonalView && token ? () => loadFunnelTransfers(token) : undefined
							}
							onOpenInviteModal={() => handleOpenInviteModal('dashboard_cta')}
							currentAge={currentAge}
							onNavigateToContributions={() => {
								setActiveTab(userMyContributions);
								navigate('/dashboard/my-contributions-individual');
							}}
							contributionPresetDelta={personalContributionPreset}
							onContributionPresetConsumed={() => setPersonalContributionPreset(null)}
							onNavigateToSimulators={
								isFreelance
									? (tab) => {
											setActiveTab(simulators);
											navigate(`/dashboard/simulators?tab=${tab}`);
										}
									: undefined
							}
						/>
					),
					route: 'my-benefit-individual'
				},
				// Aportaciones: solo con plan activo (individual y autónomo). Durante la
				// apertura no tiene sentido enseñar la tabla a cero.
				...(currentUser?.user?.planStatus === UserPlanStatus.ACTIVE_IN_ARCA
					? [
							{
								key: userMyContributions,
								label: userMyContributions,
								icon: <TbPigMoney />,
								// El plan elegido baja también aquí: sin él Movimientos pedía por
								// modo y, con dos planes del mismo tipo, pintaba el más antiguo.
								component: (
									<AportacionesIndividualV2
										// Id, tipo y productos de la MISMA fila: si el plan elegido ya
										// no está en la lista, un id suelto se pediría sin tipo ni
										// productos y el backend devolvería los de todos los planes.
										selectedPersonalPlanId={selectedPersonalPlan?.id ?? null}
										selectedPlanType={selectedPersonalPlan?.type ?? null}
										selectedPlanProductCodes={selectedPersonalPlan?.productCodes}
									/>
								),
								route: 'my-contributions-individual'
							}
						]
					: []),
				// "Tu plan" solo para autónomos CON plan activo y con un plan Indexa (los únicos
				// con la info de plan migrada de momento). Data-driven desde el ArcaPlan del usuario.
				...(isFreelance &&
				currentUser?.user?.planStatus === UserPlanStatus.ACTIVE_IN_ARCA &&
				freelanceArcaPlan?.manager === 'Indexa Capital' &&
				freelanceArcaPlan?.isFreelanceOnly
					? [
							{
								key: tuPlan,
								label: tuPlan,
								icon: <HiOutlineDocumentDuplicate />,
								component: <TuPlanAutonomoV2 currentAge={currentAge} />,
								route: 'tu-plan'
							}
						]
					: []),
				// Simuladores solo para autónomos CON plan activo: no tiene sentido simular
				// aportaciones antes de que el plan exista (durante el proceso de apertura).
				...(isFreelance && currentUser?.user?.planStatus === UserPlanStatus.ACTIVE_IN_ARCA
					? [
							{
								key: simulators,
								label: simulators,
								icon: <FaCalculator />,
								component: (
									<SimulatorsV2
										currentAge={currentAge}
										planCatalogSlug={selectedPersonalPlan?.planCatalogSlug ?? null}
										plansLoading={personalPlanPending}
									/>
								),
								route: 'simulators'
							}
						]
					: []),
				{
					key: myReferrals,
					label: myReferrals,
					icon: <Gift />,
					component: <MyReferralsView planStatus={currentUser?.user?.planStatus} />,
					route: 'my-referrals'
				},
				{
					key: individualSettings,
					label: individualSettings,
					icon: <MdDisplaySettings />,
					component: <AjustesUserV2 isIndividual={true} />,
					route: 'settings'
				}
			]
		: [
				{
					key: myBenefit,
					label: myBenefit,
					icon: <GoHome />,
					component: (
						<MiBeneficioV2
							userName={currentUser?.user.name || ''}
							currentAge={currentAge}
							isIndividual={isIndividual}
							companyName={resolveCompanyName(currentUser)}
							onNavigate={(route) => navigate(route)}
							hasPendingPersonalPlan={pendingPersonalPlan}
							pendingPersonalPlanLocked={pendingPersonalPlanLocked}
							onOpenPendingPersonalPlan={() => {
								setViewType(ViewType.INDIVIDUAL);
								navigateToDefaultTab(ViewType.INDIVIDUAL);
							}}
							contributionPresetDelta={employmentContributionPreset}
							onContributionPresetConsumed={() => setEmploymentContributionPreset(null)}
						/>
					),
					route: 'my-benefit'
				},
				{
					key: userMyContributions,
					label: userMyContributions,
					icon: <TbPigMoney />,
					component: <ConfigurarBeneficioV2 />,
					route: 'my-contributions'
				},
				{
					key: employmentPlan,
					label: employmentPlan,
					icon: <HiOutlineDocumentDuplicate />,
					component: <PlanEmpleoV2 />,
					route: 'employment-pension-plan'
				},
				// UX SCAFFOLD (tarea Notion "Definimos MVP rescates") — beneficiaria/
				// jubilada: ya no aporta, así que Planificador/IRPF/Inflación no
				// aplican, y el simulador de Rescate ya es accesible desde el modal
				// de "Mi beneficio" — la sección entera de Simuladores no aporta nada.
				{
					key: simulators,
					label: simulators,
					icon: <FaCalculator />,
					component: <SimulatorsV2 currentAge={currentAge} />,
					route: 'simulators'
				},
				{
					key: userSettings,
					label: userSettings,
					icon: <MdDisplaySettings />,
					component: <AjustesUserV2 />,
					route: 'settings'
				}
			];
	const userTabs: Tab[] = isPendingPersonalView ? allUserTabs.slice(0, 1) : allUserTabs;

	// Navigate to staff tab from InicioAdmin CTA
	const handleNavigateToPlantilla = () => {
		setActiveTab(staff);
		navigate('/dashboard/staff');
	};

	// Navigate to contributions tab from InicioAdmin chart CTA
	const handleNavigateToContributions = () => {
		setActiveTab(adminMyContributions);
		navigate('/dashboard/contributions');
	};

	const adminTabs: Tab[] = [
		{
			key: home,
			label: home,
			icon: <GoHome />,
			component: (
				<InicioAdminV2
					userName={currentUser?.user?.name || ''}
					onNavigateToPlantilla={handleNavigateToPlantilla}
					onNavigateToContributions={handleNavigateToContributions}
				/>
			),
			route: 'home'
		},
		{
			key: staff,
			label: staff,
			icon: <PiUsersFourLight />,
			component: <PlantillaV2 />,
			route: 'staff'
		},
		{
			key: adminMyContributions,
			label: adminMyContributions,
			icon: <TbPigMoney />,
			component: <AdminContribucionesV2 />,
			route: 'contributions'
		},
		{
			key: setBenefit,
			label: setBenefit,
			icon: <HiOutlineDocumentDuplicate />,
			component: <ConfigurarBeneficioAdminV2 />,
			route: 'set-benefit'
		},
		{
			key: billing,
			label: billing,
			icon: <TbFileInvoice />,
			component: <AdminBillingV2 />,
			route: 'billing'
		},
		{
			key: adminSettings,
			label: adminSettings,
			icon: <MdDisplaySettings />,
			component: <AjustesAdminV2 />,
			route: 'settings'
		}
	];

	/** Solo hay Resumen con los planes ya llegados; mientras, la lista vacía. */
	const planesConocidos = plans ?? [];
	/** Al entrar a un plan desde el Resumen: la misma navegación que el selector de planes. */
	const verPlanDesdeResumen = (planId: string) =>
		navigateToPlan(planesConocidos, planId, {
			setViewType,
			navigateToDefaultTab,
			onSelectPersonalPlan: setSelectedPersonalPlanId,
			onOpenPlan: marcarPlanVisto
		});

	/**
	 * El Resumen y la Configuración de la persona (decidido el 16/10): a cada plan se entra desde su
	 * card o desde el selector, y dentro del plan están sus Movimientos.
	 */
	const resumenTabs: Tab[] = [
		{
			key: resumenTabKey,
			label: resumenTabKey,
			icon: <GoHome />,
			component: (
				<ResumenMulticuenta
					plans={planesConocidos}
					pendingPersonalPlan={pendingPersonalPlan}
					pendingPlanArcaId={pendingPlanArcaId}
					onVerPlan={verPlanDesdeResumen}
					salario={currentUser?.user?.grossAnnualSalary}
					aportacionEmpresa={currentUser?.calculatedEmployerContribution}
				/>
			),
			route: 'resumen'
		},
		{
			key: isIndividual ? individualSettings : userSettings,
			label: isIndividual ? individualSettings : userSettings,
			icon: <MdDisplaySettings />,
			component: <AjustesUserV2 isIndividual={isIndividual} />,
			route: 'settings'
		}
	];

	/** Las pestañas de cada vista. */
	const tabsOfView = (type: string): Tab[] =>
		type === ViewType.ADMIN ? adminTabs : type === ViewType.RESUMEN ? resumenTabs : userTabs;

	const currentTabs = tabsOfView(viewType);

	const findTabByRoute = (route: string): Tab | undefined => {
		return currentTabs.find((tab) => tab.route === route);
	};

	const handleLogout = () => {
		clearContextAndLocalStorage();
		analytics.reset();
		navigate('/');
	};

	const handleChatClick = () => {
		setIsChatOpen((isChatOpen) => !isChatOpen);
	};

	const setActiveTabAndNavigate = (tab: Tab) => {
		setActiveTab(tab.key);
		navigate(`/dashboard/${tab.route}`);
	};

	const getActiveComponent = (activeTab: string): ReactNode | null => {
		const tabs = currentTabs;
		// `pickTabOrFirst` (no `find`): mientras `viewType` y `activeTab` se
		// sincronizan tras cambiar de vista, `activeTab` puede apuntar a un tab de
		// la vista anterior. Con `find` el área de contenido quedaba en blanco.
		const tab = pickTabOrFirst(tabs, activeTab);
		return tab ? tab.component : null;
	};

	// Rutas fijas por tipo de vista (DashboardRoute). Hardcodeadas a propósito:
	// setViewType es async, así que cuando navigateToDefaultTab se ejecuta el viewType
	// aún tiene el valor previo y userTabs[0].route podría apuntar a la ruta equivocada
	// (p.ej. estás en INDIVIDUAL, clicas Empleado → userTabs[0] sigue siendo
	// my-benefit-individual hasta el siguiente render).
	const navigateToDefaultTab = (type: string) => {
		if (type === ViewType.RESUMEN) {
			setActiveTab(resumenTabKey);
			navigate(DashboardRoute.RESUMEN);
		} else if (type === ViewType.ADMIN) {
			setActiveTab(ADMIN_DEFAULT_TAB);
			navigate(DashboardRoute.ADMIN_HOME);
		} else if (type === ViewType.INDIVIDUAL) {
			setActiveTab(myBenefitIndividual);
			navigate(DashboardRoute.MY_BENEFIT_INDIVIDUAL);
		} else if (type === ViewType.USER && isPureFreelance) {
			setActiveTab(myBenefitIndividual);
			navigate(DashboardRoute.MY_BENEFIT_INDIVIDUAL);
		} else {
			setActiveTab(USER_DEFAULT_TAB);
			navigate(DashboardRoute.MY_BENEFIT);
		}
	};

	// Mostrar spinner mientras se cargan los datos y hasta saber a qué vista va. Va detrás de
	// `navigateToDefaultTab` (y de lo que no es un hook): los efectos de aterrizaje la llaman
	// mientras el spinner sigue en pantalla, y en un render que sale antes no existiría.
	if (loadingUser || !destinoDecidido) {
		return (
			<div className="h-screen flex items-center justify-center">
				<Loader2 className="w-12 h-12 animate-spin text-primary" />
			</div>
		);
	}

	/**
	 * Quién es, para las dos barras de navegación.
	 *
	 * 🔴 Aquí y no dentro de ellas: la navegación no tiene por qué saber cómo viene el usuario
	 * de la API. Antes el sidebar recibía el `CurrentUserI` entero y derivaba esto él mismo,
	 * mientras la barra de móvil ya recibía las primitivas — dos formas distintas para el mismo
	 * dato. Esta es además la protección contra un usuario a medio cargar (`getCurrentUser`
	 * tarda): con los encadenamientos opcionales, hasta que vuelve se pinta sin nombre y sin
	 * correo en lugar de romperse. Quién puede administrar ya no se deriva aquí: lo hace el
	 * adaptador, a partir de los roles que se le pasan.
	 */
	const nombreCompleto =
		`${currentUser?.user?.name ?? ''} ${currentUser?.user?.firstSurname ?? ''}`.trim();
	const correo = currentUser?.user?.email ?? '';

	/**
	 * Lo que las dos barras necesitan saber, resuelto una sola vez.
	 *
	 * 🔴 Antes cada barra lo calculaba por su cuenta, con las mismas cincuenta líneas copiadas
	 * en las dos. Además de la duplicación, eso obligaba a que la navegación conociera
	 * `ViewType`, los roles y el enrutado — lo que la hacía imposible de sustituir por los
	 * bloques de Proa, que reciben todo por props.
	 */
	const datosDeNavegacion = buildNavigationData({
		plans,
		viewType,
		roles: currentUser?.user?.roles,
		selectedPersonalPlanId,
		pendingPersonalPlan,
		pendingPlanArcaId,
		resumenDisponible,
		companyId,
		companyName: resolveCompanyName(currentUser),
		translate,
		setViewType,
		navigateToDefaultTab,
		onSelectPersonalPlan: setSelectedPersonalPlanId,
		// Entrar a un plan esconde su traspaso completado en el Resumen (`planVisto`).
		onOpenPlan: marcarPlanVisto
	});

	/** Ir a Configuración. Lo usan el menú del avatar de escritorio y el de móvil. */
	const openSettingsTab = () => {
		const settingsTab = currentTabs.find((tab) => tab.route === 'settings');
		if (settingsTab) setActiveTabAndNavigate(settingsTab);
	};
	/**
	 * Sin pestaña de ajustes (la vista del plan en proceso), el menú no ofrece
	 * Configuración: sería una entrada que no lleva a ningún sitio.
	 */
	const openSettingsIfAny = currentTabs.some((tab) => tab.route === 'settings')
		? openSettingsTab
		: undefined;

	/** Lo que llevan las dos barras de Proa además de los planes (Arca-Team/product#55). */
	const textosDeLaNavegacion = textosDeNavegacion(translate, {
		sinNombreDeEmpresa: !resolveCompanyName(currentUser)
	});
	const rutaActiva = currentTabs.find((tab) => tab.key === activeTab)?.route ?? '';
	const irASeccion = (route: string) => {
		const tab = currentTabs.find((candidate) => candidate.route === route);
		if (tab) setActiveTabAndNavigate(tab);
	};
	const cuenta = {
		onOpenSettings: openSettingsIfAny,
		supportHref: SUPPORT_URL,
		onLogout: handleLogout
	};
	const {
		plans: planesDeLaNavegacion,
		adminAccess,
		showPersonalAdminSwitch,
		...controlDeNavegacion
	} = datosDeNavegacion;
	const seccionesInferiores = barraInferior(currentTabs, rutaActiva);

	const handleCloseWelcomeV2 = async () => {
		setShowWelcomeV2(false);
		try {
			await toggleFeatureFlagByName(token, companyId, userId, 'v2-platform-welcome');
		} catch {
			// silently ignore
		}
	};

	const handleOpenAIAssistant = () => {
		// Cerrar el modal de soporte si está abierto
		if (isChatOpen) {
			handleChatClick();
		}

		// Abrir el modal del asistente IA
		setIsAIAssistantOpen(true);
	};

	const handleOpenInviteModal = (source: 'sidebar' | 'mobile_nav' | 'dashboard_cta') => {
		setInviteModalSource(source);
		setIsInviteModalOpen(true);
	};

	return (
		/*
		 * `border-y-0`: el hueco del notch y del indicador de inicio ya lo reservan las barras fijas
		 * del móvil (cada una trae el suyo). Sin él, la regla de pantallas completas de `index.css`
		 * lo devolvía otra vez con un borde y quedaba una franja en blanco bajo la barra en la app.
		 * Lo vigila tooling/zonasSeguras.test.ts.
		 */
		<div className="min-h-screen border-y-0 bg-background">
			{/* Sidebar de Proa (`arca-nav`) - oculto en móviles */}
			{!isMobileScreen && (
				<div className="fixed left-0 top-0 z-20">
					<Sidebar
						userName={nombreCompleto}
						userEmail={correo}
						plans={planesDeLaNavegacion}
						adminAccess={adminAccess}
						showPersonalAdminSwitch={showPersonalAdminSwitch}
						navItems={seccionesDeNavegacion(currentTabs)}
						activeRoute={rutaActiva}
						onNavigate={irASeccion}
						cuenta={cuenta}
						textos={textosDeLaNavegacion}
						isMenuOpen={isMenuOpen}
						onToggleMenu={() => setIsMenuOpen(!isMenuOpen)}
						{...controlDeNavegacion}
					/>
				</div>
			)}

			{/* En móvil, la barra de arriba (cuenta y plan). Trae su propio hueco debajo. */}
			{isMobileScreen && (
				<MobileTopBar
					userName={nombreCompleto}
					plans={planesDeLaNavegacion}
					adminAccess={adminAccess}
					showPersonalAdminSwitch={showPersonalAdminSwitch}
					cuenta={cuenta}
					textos={textosDeLaNavegacion}
					{...controlDeNavegacion}
				/>
			)}

			{/* Contenido principal con margen para el sidebar */}
			<main
				className={`transition-all duration-300 ${
					!isMobileScreen ? (isMenuOpen ? 'ml-[300px]' : 'ml-[80px]') : 'ml-0'
				}`}
			>
				{/*
				 * En móvil las dos barras son FIJAS, así que el contenido necesita hueco
				 * arriba y abajo. Va aquí, en un único sitio, y no dentro de cada barra:
				 * montadas fuera del contenido, sus espaciadores no empujaban nada y el
				 * de abajo se sumaba a este relleno. `pt-14` cuadra con la altura fija
				 * de la barra de arriba.
				 */}
				{/* `flex flex-col` + `min-h-screen`: es lo que permite que el banner de datos de contacto
				    se pegue abajo con `mt-auto` aunque el contenido sea un spinner. Con el contenido
				    corto, un `sticky` no tiene de dónde agarrarse y el banner quedaba a media altura. */}
				{/* En móvil, la pantalla menos las dos barras de Proa (56 px + área segura arriba, 64 px +
				    área segura abajo), que traen su propio hueco fuera de aquí: con `min-h-screen` la página
				    tenía siempre ~120 px de scroll de más. */}
				<div
					className={`flex flex-col p-4 ${
						isMobileScreen
							? 'min-h-[calc(100dvh-7.5rem-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))]'
							: 'min-h-screen'
					}`}
				>
					{/*
					 * 🔴 `w-full` no es decorativo, y el envoltorio tampoco: sin ellos este contenedor
					 * desbordaba la pantalla en móvil.
					 *
					 * En un contenedor flex, un margen automático en el eje transversal ANULA el
					 * `stretch`. Varias pantallas tienen la raíz en `max-w-2xl mx-auto`
					 * (AjustesUserV2.tsx:446 y sus equivalentes de admin), así que al pasar este
					 * contenedor de bloque a `flex flex-col` dejaron de estirarse al ancho
					 * disponible y pasaron a medir por su contenido, topadas por `max-w-2xl`
					 * —672 px—. Con un viewport de 390, la página entera se iba a 688.
					 *
					 * El efecto visible no era el ancho: la tira de pestañas de ajustes tiene
					 * `overflow-x-auto`, y al caberle todo dejó de hacer scroll y se desplegó
					 * entera, arrastrando las tarjetas. Medido en un navegador a 390 px:
					 * bloque 390 · flex 670 · flex con este envoltorio 390.
					 *
					 * Lo cazó la red visual: tres referencias de móvil pasaron de 390 a 688/650 px
					 * de ancho. Ningún test lo veía — jsdom no maqueta.
					 */}
					<div className="w-full grow">{getActiveComponent(activeTab)}</div>
					{/* Banner visible solo en la vista USER (no en vista ADMIN) y fuera de configuración */}
					{viewType === ViewType.USER && !location.pathname.includes('settings') && (
						<ContactDataBanner user={currentUser?.user} />
					)}
				</div>
			</main>

			{/* En móvil, la barra de abajo: solo secciones (cuatro y «Más»). Trae su propio hueco. */}
			{isMobileScreen && (
				<MobileBottomNav
					activeRoute={rutaActiva}
					items={seccionesInferiores.items}
					overflowItems={seccionesInferiores.overflowItems}
					onNavigate={irASeccion}
					textos={textosDeLaNavegacion}
				/>
			)}

			{/* TODO: Botón de contacto flotante deshabilitado - soporte se accede desde Configuración
			{isMobileScreen ? (
				<DraggableMobileChatButton onClick={handleChatClick} />
			) : (
				<button
					onClick={handleChatClick}
					className="fixed bottom-5 right-5 z-999 w-[60px] h-[60px] rounded-full bg-secondary text-white hover:bg-secondary/90 flex items-center justify-center shadow-lg transition-colors"
				>
					<AiOutlineMessage size="24px" />
				</button>
			)}
			*/}

			<WelcomeV2Modal isOpen={showWelcomeV2} onClose={handleCloseWelcomeV2} />

			{/* El modal tiene que estar montado siempre que el botón «Invita y gana»
			    pueda estar en pantalla: la vista individual lo pinta por flags
			    (`showIndividualTabs`, sin mirar los planes), y un mixto en vista
			    Empleado/Admin también tiene acceso por su plan personal. Sin esto el
			    click sería silencioso. Con la lista de planes sin cargar y fuera de
			    la vista individual no se monta (fail-closed), igual que antes no se
			    montaba sin el rol `individual`. */}
			{currentUser?.user && (showIndividualTabs || personalPlans.length > 0) && (
				<InviteFriendModal
					open={isInviteModalOpen}
					onOpenChange={setIsInviteModalOpen}
					source={inviteModalSource}
					planStatus={currentUser.user.planStatus}
				/>
			)}

			<AnnounceNewFeatureModal
				featureFlagName={announceModalParams?.featureFlagName}
				isOpen={announceModalParams?.isOpen}
				onClose={() => setAnnounceModalParams({ ...announceModalParams, isOpen: false })}
				mainRoleForFeature={announceModalParams?.mainRoleForFeature}
				title={announceModalParams.title}
				description={announceModalParams.description}
				imageSrc={announceModalParams?.imageSrc}
				path={announceModalParams?.path}
				link={announceModalParams?.link}
			/>

			<ContactSupportModal
				isOpen={isChatOpen}
				onClose={handleChatClick}
				isMobileScreen={isMobileScreen || false}
				translate={translate}
				onOpenAIAssistant={handleOpenAIAssistant}
			/>

			{/* Modal del asistente IA */}
			<AIAssistantModal
				isOpen={isAIAssistantOpen}
				onClose={() => setIsAIAssistantOpen(false)}
				userId={userId}
			/>
		</div>
	);
};

export default Dashboard;
